/**
 * Beta signup page — schools can sign up for the founding school program.
 * Creates a Lead (with BETA_SIGNUP source) and, in Phase 2, a Deal in
 * the sales pipeline.
 *
 * Lives outside `_auth/` and `_app/` layout groups.
 */

import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { data, Link, Form, useActionData } from 'react-router'
import { z } from 'zod'
import logoUrl from '#app/assets/favicons/favicon.svg'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { processLeadSignup } from '#app/utils/lead.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { type Route } from './+types/beta-signup.ts'

const BetaSignupSchema = z.object({
	email: z
		.string({ required_error: 'Vul een e-mailadres in.' })
		.email('Vul een geldig e-mailadres in.'),
	name: z
		.string({ required_error: 'Vul je naam in.' })
		.min(2, 'Naam moet minimaal 2 tekens zijn.'),
	schoolName: z
		.string({ required_error: 'Vul de naam van je school in.' })
		.min(2, 'Schoolnaam moet minimaal 2 tekens zijn.'),
	role: z.enum(
		['docent', 'sectievoorzitter', 'ict-coordinator', 'schoolleider'],
		{ required_error: 'Selecteer je rol.' },
	),
	studentCount: z.enum(['<100', '100-300', '300-500', '500+'], {
		required_error: 'Selecteer het aantal leerlingen.',
	}),
})

export const meta: Route.MetaFunction = () => [
	{ title: 'Word founding school — Muziekdocent AI' },
	{
		name: 'description',
		content:
			'Word een van de eerste 50 founding schools. 60 dagen gratis, 3 jaar prijsgarantie.',
	},
]

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, { schema: BetaSignupSchema })

	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply(), success: false },
			{ status: 400 },
		)
	}

	const { email, name, schoolName } = submission.value

	await processLeadSignup({
		email,
		name,
		schoolName,
		source: 'BETA_SIGNUP',
	})

	// TODO Phase 2: create Deal in sales pipeline with estimated value

	return data({
		result: submission.reply({ resetForm: true }),
		success: true,
	})
}

export default function BetaSignupPage() {
	const isPending = useIsPending()
	const actionData = useActionData<typeof action>()
	const [form, fields] = useForm({
		id: 'beta-signup-form',
		constraint: getZodConstraint(BetaSignupSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: BetaSignupSchema })
		},
		// 'onInput' per ADR-101: blur-time revalidation shifts the submit
		// button mid-click and can lose the click.
		shouldRevalidate: 'onInput',
	})

	const isSuccess = actionData?.success === true

	return (
		<div className="flex min-h-screen flex-col items-center justify-center bg-[#18181b] px-6 py-16 text-[#e4e4e7]">
			<Link to="/" className="mb-12 flex items-center gap-2">
				<img src={logoUrl} alt="" className="h-8 w-8" />
				<span className="font-display text-[10px] text-neon-pink">
					MUZIEKDOCENT
				</span>
			</Link>

			<div className="w-full max-w-md rounded-2xl border border-white/5 bg-black/20 p-8">
				<h1 className="text-2xl font-bold text-white">
					Word founding school
				</h1>
				<p className="mt-2 text-sm text-text-muted">
					Test Muziekdocent AI 60 dagen gratis met je klas. Founding
					schools krijgen 3 jaar prijsgarantie op het huidige tarief.
				</p>

				{isSuccess ? (
					<div className="mt-8 rounded-xl border border-neon-green/20 bg-neon-green/5 p-6 text-center">
						<p className="text-sm font-medium text-neon-green">
							Aanmelding ontvangen
						</p>
						<p className="mt-2 text-sm text-text-muted">
							We nemen binnen 2 werkdagen contact op om een demo in
							te plannen en je school klaar te zetten.
						</p>
					</div>
				) : (
					<Form method="POST" {...getFormProps(form)} className="mt-8">
						<div className="space-y-4">
							<Field
								labelProps={{ children: 'Naam' }}
								inputProps={{
									...getInputProps(fields.name, { type: 'text' }),
									placeholder: 'Jan de Vries',
									autoFocus: true,
								}}
								errors={fields.name.errors}
							/>
							<Field
								labelProps={{ children: 'E-mail (school)' }}
								inputProps={{
									...getInputProps(fields.email, { type: 'email' }),
									placeholder: 'j.devries@school.nl',
								}}
								errors={fields.email.errors}
							/>
							<Field
								labelProps={{ children: 'School' }}
								inputProps={{
									...getInputProps(fields.schoolName, {
										type: 'text',
									}),
									placeholder: 'CSG De Heemgaard',
								}}
								errors={fields.schoolName.errors}
							/>
							<div>
								<label
									htmlFor={fields.role.id}
									className="text-sm font-medium text-white"
								>
									Jouw rol
								</label>
								<select
									id={fields.role.id}
									name={fields.role.name}
									defaultValue={fields.role.initialValue ?? ''}
									className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
								>
									<option value="">Selecteer...</option>
									<option value="docent">Muziekdocent</option>
									<option value="sectievoorzitter">
										Sectievoorzitter
									</option>
									<option value="ict-coordinator">
										ICT-coordinator
									</option>
									<option value="schoolleider">
										Schoolleider
									</option>
								</select>
								<ErrorList
									id={fields.role.errorId}
									errors={fields.role.errors}
								/>
							</div>
							<div>
								<label
									htmlFor={fields.studentCount.id}
									className="text-sm font-medium text-white"
								>
									Aantal leerlingen
								</label>
								<select
									id={fields.studentCount.id}
									name={fields.studentCount.name}
									defaultValue={fields.studentCount.initialValue ?? ''}
									className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
								>
									<option value="">Selecteer...</option>
									<option value="<100">Minder dan 100</option>
									<option value="100-300">100 - 300</option>
									<option value="300-500">300 - 500</option>
									<option value="500+">Meer dan 500</option>
								</select>
								<ErrorList
									id={fields.studentCount.errorId}
									errors={fields.studentCount.errors}
								/>
							</div>
						</div>
						<StatusButton
							className="mt-8 w-full"
							status={isPending ? 'pending' : (form.status ?? 'idle')}
							type="submit"
							disabled={isPending}
						>
							Aanmelden voor beta
						</StatusButton>
					</Form>
				)}
			</div>

			<p className="mt-8 text-center text-xs text-text-dim">
				We gebruiken je gegevens alleen om contact op te nemen over
				Muziekdocent AI.{' '}
				<Link to="/privacy" className="underline hover:text-white">
					Privacyverklaring
				</Link>
			</p>
		</div>
	)
}
